import { IProduct, ProductsClient } from 'src/Clients/ProductsClient';
import { create } from 'zustand';

export type ProductsStoreState = {
  products: IProduct[];
  loading: boolean;
  error: Error | null;
  getProductsByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  createProduct: (
    product: Omit<IProduct, 'id' | 'created_at' | 'updated_at'>
  ) => Promise<void>;
  updateProduct: (product: IProduct) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
};

export const useProductsStore = create<ProductsStoreState>((set) => ({
  products: [],
  loading: true,
  error: null,
  async getProductsByOrganizationId(
    organization_id: string,
    useLoading = true
  ) {
    if (useLoading) {
      set({ loading: true });
    }
    try {
      const products = await ProductsClient.getProductsByOrganizationId(
        organization_id
      );
      set({ products });
    } catch (error) {
      set({ error: error as Error });
    } finally {
      if (useLoading) {
        set({ loading: false });
      }
    }
  },
  async createProduct(
    product: Omit<IProduct, 'id' | 'created_at' | 'updated_at'>
  ) {
    set({ loading: true });
    try {
      const newProduct = await ProductsClient.createProduct(product);
      set((state) => ({
        ...state,
        products: [...state.products, newProduct],
      }));
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async updateProduct(product: IProduct) {
    set({ loading: true });
    try {
      const updatedProduct = await ProductsClient.updateProduct(product);
      set((state) => ({
        ...state,
        products: state.products.map((p) =>
          p.id === updatedProduct.id ? updatedProduct : p
        ),
      }));
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
  async deleteProduct(id: string) {
    set({ loading: true });
    try {
      await ProductsClient.deleteProduct(id);
      set((state) => ({
        ...state,
        products: state.products.filter((p) => p.id !== id),
      }));
    } catch (error) {
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },
}));
